import { useState } from "react";

import { Navigate } from "react-router-dom";

import LoadingButton from "@mui/lab/LoadingButton";
import { Button, FormControlLabel, Stack, Typography } from "@mui/material";

import useTransferWorkspaceOwnership from "@app/analytics/hooks/workspace/useTransferWorkspaceOwnership";
import useAuth from "@app/common/auth/useAuth";
import ROUTES from "@app/common/routes";
import type {
  EnterpriseWorkspace,
  EnterpriseWorkspaceUser,
} from "@app/common/workspaces/types";
import useWorkspace from "@app/common/workspaces/useWorkspace";
import { isWorkspaceOwner } from "@app/common/workspaces/workspaceUtils";
import usePageTitle from "@app/utils/hooks/usePageTitle";

import Checkbox from "@rui/Checkbox";
import { Link } from "@rui/Link";

import WorkspaceMemberAutocomplete from "./WorkspaceMemberAutocomplete";

const WorkspaceTransfer = ({
  workspace,
}: {
  workspace: EnterpriseWorkspace;
}) => {
  usePageTitle("Transfer Ownership");

  const [selectedUser, setSelectedUser] =
    useState<EnterpriseWorkspaceUser | null>(null);
  const [transferAcknowledged, setTransferAcknowledged] = useState(false);

  const transferWorkspaceOwnership = useTransferWorkspaceOwnership();

  const handleTransferOwnership = () => {
    // not possible to get here if the user is null, but appease the types
    if (selectedUser == null) return;

    transferWorkspaceOwnership.mutate({
      workspaceId: workspace.id,
      newOwnerUserId: selectedUser.userId,
    });
  };

  return (
    <Stack flexGrow={1} sx={{ bgcolor: "#FFF", p: 5 }}>
      <Stack
        spacing={3}
        sx={{
          margin: "auto",
          mt: "40px",
          maxWidth: "670px",
          color: "shades.neutral.500",
        }}
      >
        <Stack spacing={1}>
          <Typography variant="h2" color="shades.neutral.800">
            You are transferring ownership of {workspace.name} Workspace
          </Typography>
          <Typography>
            This action is permanent and cannot be undone. Once transfer is
            complete, you will still have access to the Workspace as an Admin.
          </Typography>
        </Stack>
        <Typography>
          To continue, select the person you would like to transfer Workspace
          ownership to.
        </Typography>
        <WorkspaceMemberAutocomplete
          disabled={transferWorkspaceOwnership.isPending}
          selected={selectedUser}
          onSelect={setSelectedUser}
        />
        <FormControlLabel
          control={
            <Checkbox
              sx={{ mr: 1 }}
              variant="filled"
              onChange={(event, checked) => setTransferAcknowledged(checked)}
            />
          }
          label={
            <Typography variant="bodyPrimaryReg" color="shades.neutral.800">
              I understand that I am transferring Workspace Ownership and
              privileges to the user above, including all management of the{" "}
              <Link href={ROUTES.BILLING}>plan & billing</Link>.
            </Typography>
          }
        />
        <Stack direction="row" sx={{ minHeight: "46px" }} spacing={2}>
          <LoadingButton
            variant="contained"
            disabled={selectedUser == null || !transferAcknowledged}
            onClick={handleTransferOwnership}
            loading={transferWorkspaceOwnership.isPending}
          >
            Transfer Ownership
          </LoadingButton>
          <Button
            sx={{ width: "70px" }}
            component={Link}
            to={ROUTES.workspaceSettingsSettingsById(workspace.id)}
          >
            Cancel
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};

const WorkspaceTransferAccessControl = () => {
  const { user } = useAuth();
  const { workspace, inWorkspace } = useWorkspace();

  // redirect anyone who doesn't belong
  if (!inWorkspace) {
    return <Navigate to={ROUTES.USER_SETTINGS_DEFAULT_SECTION} replace />;
  }

  if (!isWorkspaceOwner(user?.workspace)) {
    return <Navigate to={ROUTES.workspaceSettingsById(workspace.id)} replace />;
  }

  return <WorkspaceTransfer workspace={workspace} />;
};

export default WorkspaceTransferAccessControl;
